import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { Booking } from '../booking';
import { showError, showSuccessAlert } from '../../../../core/alert/sweetAlertConfig';

interface ManualMergeProps {
  show: boolean;
  onHide: () => void;
  statusColors: { [key: string]: string };
}

interface ApiResponse {
  status: string;
  statusDescription: string;
  data: {
    vehicleBookingRequests: Booking[];
    total: number;
  };
}

interface Vehicle {
  numberPlate: string;
  no_of_passengers: number;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface MergeModalState {
  show: boolean;
  suggestedDepartureTime: string;
  suggestedArrivalTime: string;
  vehicles: Vehicle[];
  drivers: Driver[];
  selectedVehicle: string;
  selectedDriver: string;
  comment: string;
  loading: boolean;
}

const ManualMerge: React.FC<ManualMergeProps> = ({ show, onHide, statusColors }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedBookings, setSelectedBookings] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [mergeModal, setMergeModal] = useState<MergeModalState>({
    show: false,
    suggestedDepartureTime: '',
    suggestedArrivalTime: '',
    vehicles: [],
    drivers: [],
    selectedVehicle: '',
    selectedDriver: '',
    comment: '',
    loading: false,
  });

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvedResponse, finalApprovedResponse, cancelledResponse] = await Promise.all([
        apiDefinitions.getApprovedBookings(1),
        apiDefinitions.finalApproved(1),
        apiDefinitions.getCancelledBookings(1),
      ]);

      const allBookings: Booking[] = [];

      const approvedData = approvedResponse.data as ApiResponse;
      if (approvedData.status === 'S1000' && Array.isArray(approvedData.data.vehicleBookingRequests)) {
        allBookings.push(...approvedData.data.vehicleBookingRequests.filter(b => b.status === 'APPROVED'));
      }

      const finalApprovedData = finalApprovedResponse.data as ApiResponse;
      if (finalApprovedData.status === 'S1000' && Array.isArray(finalApprovedData.data.vehicleBookingRequests)) {
        allBookings.push(...finalApprovedData.data.vehicleBookingRequests.filter(b => b.status === 'FINAL_APPROVED'));
      }

      const cancelledData = cancelledResponse.data as ApiResponse;
      if (cancelledData.status === 'S1000' && Array.isArray(cancelledData.data.vehicleBookingRequests)) {
        allBookings.push(...cancelledData.data.vehicleBookingRequests.filter(b => b.status === 'CANCELLED'));
      }

      const sortedBookings = allBookings.sort((a, b) => b.id - a.id);
      setBookings(sortedBookings);

      if (sortedBookings.length === 0) {
        setError('No bookings available for display.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.status === 401
        ? 'Unauthorized: Please check your authentication token.'
        : err.response?.data?.statusDescription || 'Failed to fetch bookings.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchBookings();
      setError(null);
      setSelectedBookings([]);
    }
  }, [show]);

  const handleSelectBooking = (bookingId: number) => {
    if (selectedBookings.includes(bookingId)) {
      setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
    } else {
      setSelectedBookings([...selectedBookings, bookingId]);
    }
  };

  const calculateSuggestedTimes = (selected: Booking[]): { departureTime: string; arrivalTime: string } => {
    const departureTimes = selected
      .filter(b => b.departureTime)
      .map(b => Number(b.departureTime))
      .filter(t => !isNaN(t));
    const arrivalTimes = selected
      .filter(b => b.suggestedArrivalTime)
      .map(b => Number(b.suggestedArrivalTime))
      .filter(t => !isNaN(t));

    const suggestedDepartureTime = departureTimes.length > 0
      ? String(Math.min(...departureTimes))
      : String(Date.now());
    // Ensure arrival time is at least 1 hour after departure time
    const minArrivalTime = Number(suggestedDepartureTime) + 3600000; // 1 hour in milliseconds
    const suggestedArrivalTime = arrivalTimes.length > 0
      ? String(Math.max(Math.max(...arrivalTimes), minArrivalTime))
      : String(minArrivalTime);

    return { departureTime: suggestedDepartureTime, arrivalTime: suggestedArrivalTime };
  };

  const fetchResources = async (departureTime: string, arrivalTime: string, totalPassengers: number) => {
  if (Number(arrivalTime) <= Number(departureTime)) {
    setError('Arrival time must be after departure time.');
    showError('Arrival time must be after departure time.');
    return;
  }

  try {
    const [vehicleRes, driverRes] = await Promise.all([
      apiDefinitions.getAvailableVehicles(departureTime, arrivalTime, totalPassengers),
      apiDefinitions.getAvailableDrivers(departureTime, arrivalTime),
    ]);

    const vehicles = vehicleRes.data.data?.vehicles || [];
    const drivers = driverRes.data.data?.drivers || [];

    setMergeModal(prev => ({
      ...prev,
      vehicles,
      drivers,
      selectedVehicle: vehicles[0]?.numberPlate || '',
      selectedDriver: drivers[0]?.empNumber || '',
    }));
  } catch (err: any) {
    const errorMessage = err.message || 'Failed to fetch vehicles and drivers.';
    setError(errorMessage);
    showError(errorMessage);
  }
};


  const handleOpenMergeModal = async () => {
    if (selectedBookings.length < 2) {
      showError('Please select at least two bookings to merge.');
      return;
    }

    const selected = bookings.filter(b => selectedBookings.includes(b.id));
    const totalPassengers = selected.reduce((sum, b) => sum + (b.noOfPassengers || 0), 0);
    const { departureTime, arrivalTime } = calculateSuggestedTimes(selected);

    setMergeModal(prev => ({
      ...prev,
      show: true,
      suggestedDepartureTime: departureTime,
      suggestedArrivalTime: arrivalTime,
      comment: 'Manual merge for selected bookings',
    }));

    await fetchResources(departureTime, arrivalTime, totalPassengers);
  };

  const handleCloseMergeModal = () => {
    setMergeModal({
      show: false,
      suggestedDepartureTime: '',
      suggestedArrivalTime: '',
      vehicles: [],
      drivers: [],
      selectedVehicle: '',
      selectedDriver: '',
      comment: '',
      loading: false,
    });
  };

  useEffect(() => {
    if (mergeModal.show && mergeModal.suggestedDepartureTime && mergeModal.suggestedArrivalTime) {
      const selected = bookings.filter(b => selectedBookings.includes(b.id));
      const totalPassengers = selected.reduce((sum, b) => sum + (b.noOfPassengers || 0), 0);
      fetchResources(mergeModal.suggestedDepartureTime, mergeModal.suggestedArrivalTime, totalPassengers);
    }
  }, [mergeModal.suggestedDepartureTime, mergeModal.suggestedArrivalTime]);

  const handleMergeSubmit = async () => {
    if (!mergeModal.selectedVehicle || !mergeModal.selectedDriver) {
      showError('Please select a vehicle and driver.');
      return;
    }

    setMergeModal(prev => ({ ...prev, loading: true }));

    try {
      const payload = {
        bookingIds: selectedBookings,
        numberPlate: mergeModal.selectedVehicle,
        empNumber: mergeModal.selectedDriver,
        suggestedDepartureTime: Number(mergeModal.suggestedDepartureTime),
        suggestedArrivalTime: Number(mergeModal.suggestedArrivalTime),
        comment: mergeModal.comment,
        };


      const response = await apiDefinitions.mergeVehicleBookingsManually(payload);
      if (response.data.status === 'S1000') {
        showSuccessAlert("Bookings merged successfully");
        handleCloseMergeModal();
        fetchBookings();
        setSelectedBookings([]);
      } else {
        throw new Error(response.data.statusDescription || 'Failed to merge bookings.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.statusDescription || 'Failed to merge bookings.';
      showError(errorMessage);
    } finally {
      setMergeModal(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        aria-labelledby="manual-merge-modal"
        aria-modal="true"
        autoFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="manual-merge-modal">Vehicle Bookings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && bookings.length === 0 && (
            <div className="alert alert-info">No bookings available for display.</div>
          )}
          {!loading && !error && bookings.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>
                      <Form.Check
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBookings(bookings.filter(b => b.status !== 'CANCELLED').map(b => b.id));
                          } else {
                            setSelectedBookings([]);
                          }
                        }}
                        checked={selectedBookings.length === bookings.filter(b => b.status !== 'CANCELLED').length}
                        disabled={bookings.filter(b => b.status !== 'CANCELLED').length === 0}
                        aria-label="Select all non-cancelled bookings"
                      />
                    </th>
                    <th>Booking ID</th>
                    <th>Employee</th>
                    <th>Status</th>
                    <th>Passengers</th>
                    <th>Departure Time</th>
                    <th>Arrival Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedBookings.includes(booking.id)}
                          onChange={() => handleSelectBooking(booking.id)}
                          aria-label={`Select booking ${booking.id}`}
                        />
                      </td>
                      <td>{booking.id}</td>
                      <td>{booking.user?.name || 'N/A'}</td>
                      <td>
                        <span className={statusColors[booking.status] || 'badge bg-secondary'}>
                          {booking.status || 'N/A'}
                        </span>
                      </td>
                      <td>{booking.noOfPassengers || 'N/A'}</td>
                      <td>
                        {booking.departureTime
                          ? new Date(Number(booking.departureTime)).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td>
                        {booking.arrivalTime
                          ? new Date(Number(booking.arrivalTime)).toLocaleString()
                          : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleOpenMergeModal}
            disabled={selectedBookings.length < 2 || loading}
            aria-label="Merge selected bookings"
          >
            Merge
          </Button>
          <Button
            variant="primary"
            onClick={onHide}
            disabled={loading}
            aria-label="Close vehicle bookings modal"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={mergeModal.show}
        onHide={handleCloseMergeModal}
        centered
        aria-labelledby="merge-details-modal"
        aria-modal="true"
        autoFocus={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title id="merge-details-modal">Merge Bookings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Suggested Departure Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={mergeModal.suggestedDepartureTime
                  ? new Date(Number(mergeModal.suggestedDepartureTime)).toISOString().slice(0, 16)
                  : ''}
                onChange={(e) => {
                  const timestamp = new Date(e.target.value).getTime();
                  if (!isNaN(timestamp)) {
                    setMergeModal(prev => ({ ...prev, suggestedDepartureTime: String(timestamp) }));
                  }
                }}
                disabled={mergeModal.loading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Suggested Arrival Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={mergeModal.suggestedArrivalTime
                  ? new Date(Number(mergeModal.suggestedArrivalTime)).toISOString().slice(0, 16)
                  : ''}
                onChange={(e) => {
                  const timestamp = new Date(e.target.value).getTime();
                  if (!isNaN(timestamp)) {
                    setMergeModal(prev => ({ ...prev, suggestedArrivalTime: String(timestamp) }));
                  }
                }}
                disabled={mergeModal.loading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select Vehicle</Form.Label>
              <Form.Select
                value={mergeModal.selectedVehicle}
                onChange={(e) => setMergeModal(prev => ({ ...prev, selectedVehicle: e.target.value }))}
                disabled={mergeModal.loading || mergeModal.vehicles.length === 0}
              >
                {mergeModal.vehicles.length === 0 ? (
                  <option value="">No vehicles available</option>
                ) : (
                  mergeModal.vehicles.map((vehicle) => (
                    <option key={vehicle.numberPlate} value={vehicle.numberPlate}>
                      {vehicle.numberPlate} 
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select Driver</Form.Label>
              <Form.Select
                value={mergeModal.selectedDriver}
                onChange={(e) => setMergeModal(prev => ({ ...prev, selectedDriver: e.target.value }))}
                disabled={mergeModal.loading || mergeModal.drivers.length === 0}
              >
                {mergeModal.drivers.length === 0 ? (
                  <option value="">No drivers available</option>
                ) : (
                  mergeModal.drivers.map((driver) => (
                    <option key={driver.empNumber} value={driver.empNumber}>
                      {driver.name} ({driver.empNumber})
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={mergeModal.comment}
                onChange={(e) => setMergeModal(prev => ({ ...prev, comment: e.target.value }))}
                disabled={mergeModal.loading}
                placeholder="Enter any comments for the merge"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleCloseMergeModal}
            disabled={mergeModal.loading}
            aria-label="Close merge details modal"
          >
            Close
          </Button>
          <Button
            variant="secondary"
            onClick={handleMergeSubmit}
            disabled={mergeModal.loading}
            aria-label="Submit merge request"
          >
            {mergeModal.loading ? 'Merging...' : 'Merge'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManualMerge;